import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/site-content';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9sraj58o1vn02ekukv0';

export default function AboutPage() {
  const [submitting, setSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg && serverMsg.includes('spam')) {
        setFormError('提交失败，请稍后再试。');
        setFormStatus('error');
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试。');
        setFormStatus('error');
      }
    } catch (err) {
      setFormError('网络错误，请检查您的连接后重试。');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20 md:pt-24 bg-background-50">
      <nav className="border-b border-background-200" aria-label="面包屑导航">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><span className="text-foreground-800">关于我们</span></li>
          </ol>
        </div>
      </nav>

      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20fashion%20background%20with%20soft%20flowing%20fabric%20in%20warm%20burgundy%20cream%20and%20gold%20tones%2C%20elegant%20minimalist%20composition%2C%20luxury%20editorial%20aesthetic&width=1920&height=500&seq=about-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
            关于<span className="text-accent-400">我们</span>
          </h1>
          <p className="text-background-200 text-sm md:text-base max-w-lg mx-auto">
            每一缕裙摆之下，都藏着一个不愿被驯服的灵魂。
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              品牌<span className="text-primary-600">故事</span>
            </h2>
            <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
              <p>
                「裙子下面是野兽」诞生于一个简单的信念：每一位女性都是一首未被唱完的歌，一面未被打磨的宝石。
                我们拒绝将女性定义为温柔或强势、优雅或狂野——因为真正的美，从来不是非此即彼的选择题。
              </p>
              <p>
                我们的名字来自一个意象：当风吹起裙摆，你看到的不是脆弱，而是力量；不是顺从，而是自由。
                每一件衣服、每一种搭配、每一个风格选择，都是你向世界宣告「这就是我」的方式。
              </p>
              <p>
                我们相信，时尚不仅仅是关于衣服——它关于身份、关于表达、关于解放。
                在这里，你不会找到任何「你应该怎么穿」的说教，只会找到关于「你可以成为谁」的灵感。
              </p>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              我们的<span className="text-primary-600">使命</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-heart-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">启发表达</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  通过深度的时尚内容和风格指南，激发每一位女性找到属于自己的表达方式。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-shield-star-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">打破框架</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  挑战社会对女性气质的刻板定义，让每个女人都能在优雅与野性之间自由穿行。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                  <i className="ri-global-line text-xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">连接社群</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  构建一个充满力量的女性社群，让每一位成员都能在这里找到共鸣与支持。
                </p>
              </div>
            </div>
          </div>

          <div id="testimonials" className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              用户<span className="text-primary-600">口碑</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed italic">
                    {t.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div id="contact">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              联系<span className="text-primary-600">我们</span>
            </h2>
            <div className="bg-background-100 rounded-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5 max-w-lg">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                {formStatus === 'success' && (
                  <div className="p-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                    感谢您的留言！我们会尽快与您联系。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-3 rounded-md bg-primary-100 text-primary-700 text-sm">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-3 bg-primary-500 text-background-50 font-medium text-sm tracking-wider rounded-md hover:bg-primary-600 disabled:opacity-50 transition-all duration-300 whitespace-nowrap"
                >
                  {submitting ? '提交中...' : '发送留言'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}